import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useState, useEffect, useRef } from 'react';
import { BotanicalCorner, GoldLineDivider, FlowingWaves } from '@/components/feature/DrawingLines';

// ── Data ──────────────────────────────────────────────────────────────────────

const processSteps = [
  {
    step: '01',
    title: '온라인 가맹 문의',
    period: '1일',
    icon: 'ri-mail-send-line',
    desc: '홈페이지 또는 전화를 통해 가맹 문의를 접수합니다. 담당 컨설턴트가 24시간 내 연락드립니다.',
    details: ['온라인 문의 폼 작성', '희망 지역 및 규모 기재', '담당자 배정 및 연락'],
  },
  {
    step: '02',
    title: '1:1 창업 상담',
    period: '1~3일',
    icon: 'ri-discuss-line',
    desc: '본사 방문 또는 화상 미팅을 통해 맞춤형 창업 플랜을 수립합니다. 수익 시뮬레이션을 함께 검토합니다.',
    details: ['본사 방문 또는 화상 상담', '상권 분석 리포트 제공', '맞춤 수익 시뮬레이션'],
  },
  {
    step: '03',
    title: '상권 분석 & 입지 선정',
    period: '1~2주',
    icon: 'ri-map-pin-2-line',
    desc: '전문 상권 분석팀이 희망 지역의 유동인구, 경쟁 현황, 임대 시세를 분석하여 최적 입지를 추천합니다.',
    details: ['빅데이터 기반 상권 분석', '경쟁 스튜디오 현황 조사', '임대 협상 지원'],
  },
  {
    step: '04',
    title: '가맹 계약 체결',
    period: '3~5일',
    icon: 'ri-file-text-line',
    desc: '표준 가맹 계약서를 검토하고 서명합니다. 계약 전 법무팀 검토 및 충분한 숙려 기간을 보장합니다.',
    details: ['표준 계약서 사전 제공', '법무 검토 지원', '14일 숙려 기간 보장'],
  },
  {
    step: '05',
    title: '인테리어 & 시공',
    period: '3~4주',
    icon: 'ri-store-2-line',
    desc: '선율 브랜드 가이드라인에 맞춘 인테리어 시공을 진행합니다. 본사 협력 업체를 통해 비용을 절감합니다.',
    details: ['브랜드 인테리어 가이드 제공', '협력 시공사 연결 (20% 절감)', '시공 감리 지원'],
  },
  {
    step: '06',
    title: '아티스트 교육 과정',
    period: '4~6주',
    icon: 'ri-graduation-cap-line',
    desc: '본사 아침데미에서 기술 교육, 위생 교육, 고객 응대 교육을 이수합니다. 수료 후 인증서를 발급합니다.',
    details: ['라인워크 집중 기술 교육', 'Medical Grade 위생 교육', '고객 상담 & CS 교육'],
  },
  {
    step: '07',
    title: '오픈 준비 & 마케팅',
    period: '1~2주',
    icon: 'ri-megaphone-line',
    desc: '본사 마케팅팀이 SNS 계정 세팅, 오픈 이벤트 기획, 지역 광고를 지원합니다.',
    details: ['인스타그램 계정 세팅', '오픈 이벤트 기획 지원', '지역 타겟 광고 집행'],
  },
  {
    step: '08',
    title: '그랜드 오픈',
    period: 'D-Day',
    icon: 'ri-sparkling-line',
    desc: '드디어 선율 가맹점으로 오픈합니다. 본사 담당자가 오픈 당일 현장 지원을 나옵니다.',
    details: ['오픈 당일 본사 현장 지원', '첫 달 밀착 운영 컨설팅', '온라인 리뷰 관리 지원'],
  },
];

// ── Plan Data ─────────────────────────────────────────────────────────────────

const plans = [
  {
    id: 'standard',
    type: 'A',
    badge: '실속형',
    badgeEn: 'Standard',
    tagline: '가성비 위주의 핵심 인테리어로\n초기 투자비를 최소화한 실용적 모델',
    perPyeong: '평당 인테리어 약 180만 원',
    highlight: false,
    recommended: false,
    totalLabel: '3,900만 원',
    totalNum: 3900,
    roi: '3개월',
    items: [
      { label: '가맹비', amount: '5,000,000원', note: '브랜드 사용권 및 초기 지원비' },
      { label: '교육비', amount: '3,000,000원', note: '6주 아침데미 전과정 포함' },
      { label: '간판비', amount: '3,000,000원', note: '브랜드 표준 간판 제작 및 설치 (공통)' },
      { label: '공간 (인테리어)', amount: '18,000,000원', note: '도면 및 공사비 포함 · 10평 기준' },
      { label: '세팅 (장비 / 초도물품)', amount: '10,000,000원', note: '핵심 필수 장비 중심 패키지', detail: true },
    ],
    extras: ['냉난방기', '상가 보증금', '외관 갈바 공사', '철거 비용'],
  },
  {
    id: 'gallery',
    type: 'B',
    badge: '프리미엄형',
    badgeEn: 'Gallery',
    tagline: '하이엔드 갤러리 풀체인지로\n브랜드의 압도적인 분위기를 구현한 플래그십 모델',
    perPyeong: '평당 인테리어 약 250만 원 이상',
    highlight: true,
    recommended: true,
    totalLabel: '5,100만 원',
    totalNum: 5100,
    roi: '4개월',
    items: [
      { label: '가맹비', amount: '5,000,000원', note: '브랜드 사용권 및 초기 지원비' },
      { label: '교육비', amount: '3,000,000원', note: '6주 아침데미 전과정 포함' },
      { label: '간판비', amount: '3,000,000원', note: '브랜드 표준 간판 제작 및 설치 (공통)' },
      { label: '공간 (인테리어)', amount: '25,000,000원', note: '도면 및 공사비 포함 · 10평 기준' },
      { label: '세팅 (장비 / 초도물품)', amount: '15,000,000원', note: '하이엔드 전동 장비 및 풀 멸균 시스템', detail: true },
    ],
    extras: ['냉난방기', '상가 보증금', '외관 갈바 공사', '철거 비용'],
  },
  {
    id: 'self',
    type: 'C',
    badge: '자율 시공형',
    badgeEn: 'Self',
    tagline: '점주가 직접 시공 업체를 선정하여\n인테리어 비용을 극최소화할 수 있는 합리적 모델',
    perPyeong: '본사 디자인 가이드 및 감리비 필수',
    highlight: false,
    recommended: false,
    totalLabel: '2,600만 원 + α',
    totalNum: 2600,
    roi: '2개월',
    items: [
      { label: '가맹비', amount: '5,000,000원', note: '브랜드 사용권 및 초기 지원비' },
      { label: '교육비', amount: '3,000,000원', note: '6주 아침데미 전과정 포함' },
      { label: '간판비', amount: '3,000,000원', note: '브랜드 표준 간판 제작 및 설치 (공통)' },
      { label: '공간 (인테리어)', amount: '점주 직접 지불', note: '점주가 직접 업체 선정 및 비용 부담' },
      { label: '디자인 (도면 및 감리비)', amount: '5,000,000원', note: '본사 매뉴얼 준수 + 감리 3회 포함' },
      { label: '세팅 (장비 / 초도물품)', amount: '10,000,000원', note: '핵심 필수 장비 중심 패키지', detail: true },
    ],
    extras: ['냉난방기', '상가 보증금', '외관 갈바 공사', '철거 비용', '인테리어 실비 (α)'],
    selfBuild: true,
  },
];

const settingDetails = {
  equipment: {
    title: '전문 작업 장비',
    items: ['하이엔드 무선 머신 2대', '아티스트 전용 체어', '고광량 무영 조명'],
  },
  hygiene: {
    title: '위생 및 스튜디오 설비',
    items: ['프리미엄 전동 베드', '의료용 스테인리스 트롤리', '고압 증기 멸균기'],
  },
  consumables: {
    title: '소모품 패키지 (3개월분)',
    items: ['본사 인증 잉크 세트', '멸균 니들 세트', '위생 소모품 일체', '전용 가운'],
  },
};

const royaltyBenefits = [
  { icon: 'ri-global-line', title: '전국 통합 마케팅 지원', desc: '본사 SNS·검색광고 공동 집행, 월 500만 원 상당 마케팅 효과' },
  { icon: 'ri-tools-line', title: '기술 업데이트', desc: '신기법·트렌드 기술 정기 교육, 연 4회 본사 워크숍 참여권' },
  { icon: 'ri-shopping-bag-3-line', title: '점주 전용 소모품 공급가', desc: '시중가 대비 30~40% 할인된 가격으로 프리미엄 소모품 공급' },
  { icon: 'ri-shield-check-line', title: '브랜드 보호 시스템', desc: '상권 보호 정책 적용, 동일 상권 내 신규 가맹 제한' },
  { icon: 'ri-customer-service-2-line', title: '24시간 운영 지원', desc: '전담 컨설턴트 직통 연결, 긴급 상황 즉시 대응 시스템' },
  { icon: 'ri-bar-chart-2-line', title: '데이터 기반 경영 지원', desc: '월별 매출 분석 리포트 제공, 가맹점 벤치마킹 데이터 공유' },
];

const supportHighlights = [
  { icon: 'ri-bar-chart-box-line', title: '빅데이터 상권 분석', desc: '전국 유동인구 데이터 기반 최적 입지 추천' },
  { icon: 'ri-palette-line', title: '브랜드 인테리어 지원', desc: '협력 시공사 연결로 평균 20% 비용 절감' },
  { icon: 'ri-instagram-line', title: 'SNS 마케팅 지원', desc: '오픈 초기 3개월 본사 공동 마케팅 집행' },
  { icon: 'ri-customer-service-2-line', title: '전담 컨설턴트 배정', desc: '오픈 후 6개월 밀착 운영 컨설팅 제공' },
];

// ── ROI Bar Graph ─────────────────────────────────────────────────────────────

function RoiGraph({ visible, planId }: { visible: boolean; planId: string }) {
  const config = {
    standard: { investment: 3900, months: 3, color: '#1A1A1A' },
    gallery:  { investment: 5100, months: 4, color: '#C9A96E' },
    self:     { investment: 2600, months: 2, color: '#888888' },
  }[planId] ?? { investment: 3600, months: 3, color: '#1A1A1A' };

  const monthlyProfit = 1250;
  const maxVal = Math.ceil((monthlyProfit * 6) / 1000) * 1000;

  const months = Array.from({ length: 6 }, (_, i) => {
    const cum = monthlyProfit * (i + 1);
    const recovered = cum >= config.investment;
    const justRecovered = i + 1 === config.months;
    return { label: `${i + 1}개월`, cumulative: cum, recovered, justRecovered };
  });

  return (
    <div className="w-full" style={{ overflow: 'visible' }}>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-6 h-px border-t-2 border-dashed border-[#1A1A1A]/40" />
        <span className="text-[11px] text-[#888] font-medium">투자 원금 {(config.investment / 100).toFixed(0)}백만 원</span>
      </div>
      <div className="relative" style={{ paddingTop: '28px' }}>
        <div
          className="absolute left-0 right-0 border-t-2 border-dashed border-[#1A1A1A]/20 z-10 transition-all duration-700"
          style={{ bottom: `${(config.investment / maxVal) * 140}px`, opacity: visible ? 1 : 0, transitionDelay: '900ms' }}
        />
        {/* 원금 회수 뱃지: 바 컬럼 밖, 그래프 컨테이너 기준으로 absolute 배치 */}
        {months.map((m) =>
          m.justRecovered && visible ? (
            <div
              key={`badge-${m.label}`}
              className="absolute z-20 transition-all duration-500"
              style={{
                bottom: `${(m.cumulative / maxVal) * 140 + 6}px`,
                left: `${(months.indexOf(m) / months.length) * 100 + (1 / months.length) * 50}%`,
                transform: 'translateX(-50%)',
                opacity: visible ? 1 : 0,
                transitionDelay: '1300ms',
                pointerEvents: 'none',
              }}
            >
              <span
                className="text-[9px] font-semibold px-2 py-0.5 rounded-full text-white"
                style={{ background: config.color, whiteSpace: 'nowrap', display: 'block' }}
              >
                원금 회수!
              </span>
            </div>
          ) : null
        )}
        <div className="flex items-end gap-2" style={{ height: '140px' }}>
          {months.map((m, i) => (
            <div key={m.label} className="flex-1 flex flex-col items-center relative" style={{ height: '140px' }}>
              <div className="w-full absolute bottom-0 rounded-t-sm transition-all ease-out"
                style={{
                  height: visible ? `${(m.cumulative / maxVal) * 140}px` : '0px',
                  background: m.recovered ? config.color : '#E8E8E8',
                  transitionDuration: '800ms',
                  transitionDelay: `${i * 110 + 300}ms`,
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-2 mt-2">
        {months.map((m) => (
          <div key={m.label} className="flex-1 text-center">
            <span className="text-[9px] text-[#AAAAAA]">{m.label}</span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-[#CCCCCC] mt-3 leading-relaxed">
        ※ 월 순이익 1,250만 원 기준 시뮬레이션
      </p>
    </div>
  );
}

// ── Setting Details Section ───────────────────────────────────────────────────

function SettingDetailsSection({ visible }: { visible: boolean }) {
  return (
    <div
      className="mb-16 transition-all duration-700 ease-out"
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transitionDelay: '600ms' }}
    >
      <div className="bg-white border border-[#E8E8E8] p-10 md:p-14">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 pb-8 border-b border-[#F0F0F0]">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#C9A96E] mb-3">Setting Package Details</p>
            <h3
              className="text-2xl md:text-3xl text-[#1A1A1A]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
            >
              세팅 (장비 / 초도물품) 상세 내역
            </h3>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-xs text-[#AAAAAA] mb-1">실속형 세팅</p>
              <p className="text-2xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>1,000만 원</p>
            </div>
            <div className="w-px h-10 bg-[#E8E8E8]" />
            <div className="text-right">
              <p className="text-xs text-[#AAAAAA] mb-1">프리미엄형 세팅</p>
              <p className="text-2xl font-bold text-[#C9A96E]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>1,500만 원</p>
            </div>
          </div>
        </div>

        {/* 3 Columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Equipment */}
          <div>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 flex items-center justify-center border border-[#E8E8E8]">
                <i className="ri-tools-line text-[#C9A96E]" />
              </div>
              <h4 className="text-sm font-semibold text-[#1A1A1A]">{settingDetails.equipment.title}</h4>
            </div>
            <ul className="space-y-3">
              {settingDetails.equipment.items.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#C9A96E] mt-2 flex-shrink-0" />
                  <span className="text-sm text-[#555]">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Hygiene */}
          <div>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 flex items-center justify-center border border-[#E8E8E8]">
                <i className="ri-hospital-line text-[#C9A96E]" />
              </div>
              <h4 className="text-sm font-semibold text-[#1A1A1A]">{settingDetails.hygiene.title}</h4>
            </div>
            <ul className="space-y-3">
              {settingDetails.hygiene.items.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#C9A96E] mt-2 flex-shrink-0" />
                  <span className="text-sm text-[#555]">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Consumables */}
          <div>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 flex items-center justify-center border border-[#E8E8E8]">
                <i className="ri-archive-line text-[#C9A96E]" />
              </div>
              <h4 className="text-sm font-semibold text-[#1A1A1A]">{settingDetails.consumables.title}</h4>
            </div>
            <ul className="space-y-3">
              {settingDetails.consumables.items.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#C9A96E] mt-2 flex-shrink-0" />
                  <span className="text-sm text-[#555]">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Note */}
        <div className="mt-10 pt-6 border-t border-[#F0F0F0]">
          <p className="text-xs text-[#AAAAAA] leading-relaxed">
            ※ 프리미엄형은 실속형 대비 고급 사양의 장비 및 추가 설비가 포함됩니다. 정확한 사양은 상담 시 안내드립니다.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Investment Plan Section ───────────────────────────────────────────────────

function InvestmentPlanSection() {
  const { ref, visible } = useScrollAnimation(0.05);
  const roiRef = useRef<HTMLDivElement>(null);
  const [roiVisible, setRoiVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>('gallery');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setRoiVisible(true); observer.disconnect(); } },
      { threshold: 0.2 }
    );
    if (roiRef.current) observer.observe(roiRef.current);
    return () => observer.disconnect();
  }, []);

  const activePlan = plans.find((p) => p.id === selectedPlan) ?? plans[1];

  return (
    <section className="py-28 md:py-36 bg-[#FAFAFA] overflow-hidden relative" ref={ref}>
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">

        {/* ── Section Header ── */}
        <div
          className="text-center mb-20 transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5">Startup Cost Guide</p>
          <h2
            className="text-3xl md:text-4xl text-[#1A1A1A] mb-5 leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
          >
            선율(SEONYUL) 프리미엄 창업 플랜<br />
            <span className="text-[#AAAAAA]">및 비용 상세 가이드</span>
          </h2>
          <p className="text-sm text-[#888] max-w-lg mx-auto leading-relaxed">
            예비 점주님의 예산과 비전에 맞는 플랜을 선택하세요.<br />
            모든 비용은 10평 기준이며 투명하게 공개됩니다.
          </p>
          <div
            className="mx-auto mt-8 h-px bg-[#E5E5E5] transition-all duration-1000 ease-out"
            style={{ width: visible ? '80px' : '0px', transitionDelay: '400ms' }}
          />
        </div>

        {/* ── 3-Plan Cards ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {plans.map((plan, i) => (
            <div
              key={plan.id}
              className="relative flex flex-col transition-all duration-700 ease-out"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(50px)',
                transitionDelay: `${i * 130 + 200}ms`,
              }}
            >
              {plan.recommended && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                  <span className="bg-[#C9A96E] text-white text-[10px] tracking-[0.2em] uppercase px-4 py-1.5 rounded-full font-medium whitespace-nowrap">
                    Most Popular
                  </span>
                </div>
              )}

              <div
                className={`flex flex-col flex-1 border-2 transition-all duration-300 cursor-pointer ${
                  selectedPlan === plan.id
                    ? plan.highlight
                      ? 'border-[#C9A96E] bg-[#1A1A1A]'
                      : 'border-[#1A1A1A] bg-white'
                    : plan.highlight
                    ? 'border-[#C9A96E]/40 bg-[#1A1A1A]'
                    : 'border-[#E8E8E8] bg-white hover:border-[#AAAAAA]'
                }`}
                onClick={() => setSelectedPlan(plan.id)}
              >
                {/* Top accent */}
                <div className="h-1" style={{ background: plan.highlight ? '#C9A96E' : selectedPlan === plan.id ? '#1A1A1A' : '#E8E8E8' }} />

                <div className="p-8 flex flex-col flex-1">
                  {/* Type badge */}
                  <div className="flex items-start justify-between mb-5">
                    <div>
                      <span className={`text-[10px] tracking-[0.3em] uppercase font-medium block mb-1.5 ${plan.highlight ? 'text-[#C9A96E]' : 'text-[#AAAAAA]'}`}>
                        Type {plan.type} · {plan.badgeEn}
                      </span>
                      <h3
                        className={`text-2xl leading-tight ${plan.highlight ? 'text-white' : 'text-[#1A1A1A]'}`}
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                      >
                        {plan.badge}
                      </h3>
                    </div>
                    {/* ROI chip */}
                    <span className={`text-[10px] px-2.5 py-1 rounded-full whitespace-nowrap font-medium mt-1 ${
                      plan.highlight ? 'bg-[#C9A96E]/20 text-[#C9A96E]' : 'bg-[#F2F2F2] text-[#888]'
                    }`}>
                      ROI {plan.roi}
                    </span>
                  </div>

                  {/* Tagline */}
                  <p className={`text-xs leading-relaxed mb-5 whitespace-pre-line ${plan.highlight ? 'text-white/50' : 'text-[#888]'}`}>
                    {plan.tagline}
                  </p>

                  {/* Per-pyeong note */}
                  <div className={`flex items-center gap-2 mb-6 pb-6 border-b ${plan.highlight ? 'border-white/10' : 'border-[#F0F0F0]'}`}>
                    <i className={`ri-information-line text-sm ${plan.highlight ? 'text-[#C9A96E]' : 'text-[#AAAAAA]'}`} />
                    <span className={`text-[11px] ${plan.highlight ? 'text-white/40' : 'text-[#AAAAAA]'}`}>{plan.perPyeong}</span>
                  </div>

                  {/* Cost breakdown */}
                  <div className="flex flex-col gap-3 mb-6 flex-1">
                    {plan.items.map((item) => (
                      <div key={item.label} className={`p-4 ${plan.highlight ? 'bg-white/5' : 'bg-[#F8F8F8]'}`}>
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <span className={`text-[11px] font-medium leading-tight ${plan.highlight ? 'text-white/70' : 'text-[#555]'}`}>
                            {item.label}
                          </span>
                          <span className={`text-sm font-bold whitespace-nowrap tabular-nums ${
                            item.amount === '점주 직접 지불'
                              ? plan.highlight ? 'text-[#C9A96E]' : 'text-[#C9A96E]'
                              : plan.highlight ? 'text-white' : 'text-[#1A1A1A]'
                          }`}>
                            {item.amount}
                          </span>
                        </div>
                        <p className={`text-[10px] leading-relaxed ${plan.highlight ? 'text-white/30' : 'text-[#AAAAAA]'}`}>
                          {item.note}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Total */}
                  <div className={`flex items-center justify-between px-4 py-4 mb-5 ${plan.highlight ? 'bg-[#C9A96E]/15' : 'bg-[#1A1A1A]'}`}>
                    <span className={`text-xs tracking-[0.15em] uppercase font-medium ${plan.highlight ? 'text-[#C9A96E]' : 'text-white/60'}`}>
                      최종 추정 창업비
                    </span>
                    <span
                      className={`text-xl font-bold tabular-nums ${plan.highlight ? 'text-[#C9A96E]' : 'text-white'}`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {plan.totalLabel}
                    </span>
                  </div>

                  {/* Extras note */}
                  <div className={`mb-5 p-3 border ${plan.highlight ? 'border-white/10' : 'border-[#EBEBEB]'}`}>
                    <p className={`text-[10px] font-medium mb-1.5 ${plan.highlight ? 'text-white/40' : 'text-[#AAAAAA]'}`}>
                      ※ 별도 발생 항목
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {plan.extras.map((ex) => (
                        <span key={ex} className={`text-[10px] px-2 py-0.5 rounded-full ${plan.highlight ? 'bg-white/5 text-white/30' : 'bg-[#F5F5F5] text-[#AAAAAA]'}`}>
                          {ex}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* CTA button */}
                  <a
                    href="#guidebook"
                    onClick={(e) => e.preventDefault()}
                    className={`w-full py-3.5 text-xs tracking-[0.15em] uppercase font-medium rounded-full border text-center transition-all duration-300 cursor-pointer whitespace-nowrap block ${
                      plan.highlight
                        ? 'bg-[#C9A96E] border-[#C9A96E] text-white hover:bg-[#b8955a] hover:border-[#b8955a]'
                        : 'border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white'
                    }`}
                  >
                    상세 창업 가이드북 신청하기
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Royalty notice bar */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-8 px-8 py-5 bg-[#1A1A1A] mb-16 transition-all duration-700 ease-out"
          style={{ opacity: visible ? 1 : 0, transitionDelay: '700ms' }}
        >
          <div className="flex items-center gap-3">
            <i className="ri-information-line text-[#C9A96E] text-base" />
            <span className="text-xs text-white/60 tracking-wide">
              <strong className="text-white font-semibold">월 로열티 500,000원</strong> (통합 마케팅 및 광고비 포함) 별도
            </span>
          </div>
          <div className="hidden sm:block w-px h-4 bg-white/10" />
          <span className="text-xs text-white/40">냉난방기 · 상가 보증금 · 외관 갈바 공사 · 철거 비용은 현장 상황에 따라 별도 발생</span>
        </div>

        {/* ── Setting Details ── */}
        <SettingDetailsSection visible={visible} />

        {/* ── ROI Simulation (linked to selected plan) ── */}
        <div
          ref={roiRef}
          className="bg-white border border-[#E8E8E8] transition-all duration-700 ease-out mb-16"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transitionDelay: '800ms' }}
        >
          {/* Tab selector */}
          <div className="flex border-b border-[#F0F0F0]">
            {plans.map((plan) => (
              <button
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`flex-1 py-4 text-xs tracking-[0.15em] uppercase font-medium transition-all duration-200 cursor-pointer whitespace-nowrap border-b-2 ${
                  selectedPlan === plan.id
                    ? 'border-[#C9A96E] text-[#1A1A1A]'
                    : 'border-transparent text-[#AAAAAA] hover:text-[#888]'
                }`}
              >
                {plan.badge}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Left: copy */}
            <div className="p-10 md:p-14 border-b lg:border-b-0 lg:border-r border-[#F0F0F0]">
              <p className="text-xs tracking-[0.3em] uppercase text-[#C9A96E] mb-5">ROI Simulation</p>
              <h3
                className="text-2xl md:text-3xl text-[#1A1A1A] mb-4 leading-snug"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
              >
                {activePlan.badge} 창업 시<br />
                단 <span className="text-[#C9A96E]">{activePlan.roi}</span> 만에<br />
                투자 원금 회수 가능
              </h3>
              <p className="text-sm text-[#888] leading-relaxed mb-8">
                월 순이익 <strong className="text-[#1A1A1A]">1,250만 원</strong> 모델 기준,
                {activePlan.badge} 창업 비용 <strong className="text-[#1A1A1A]">{activePlan.totalLabel}</strong>은
                단 {activePlan.roi} 운영만으로 전액 회수됩니다.
              </p>

              {/* Plan comparison mini */}
              <div className="flex flex-col gap-3">
                {plans.map((p) => (
                  <div
                    key={p.id}
                    className={`flex items-center gap-3 cursor-pointer group`}
                    onClick={() => setSelectedPlan(p.id)}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0 transition-all duration-200"
                      style={{ background: selectedPlan === p.id ? (p.highlight ? '#C9A96E' : '#1A1A1A') : '#E0E0E0' }}
                    />
                    <span className={`text-xs transition-colors duration-200 ${selectedPlan === p.id ? 'text-[#1A1A1A] font-medium' : 'text-[#AAAAAA] group-hover:text-[#888]'}`}>
                      {p.badge} — {p.totalLabel}
                    </span>
                    <span className={`ml-auto text-xs font-semibold transition-colors duration-200 ${selectedPlan === p.id ? 'text-[#C9A96E]' : 'text-[#CCCCCC]'}`}>
                      ROI {p.roi}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: bar chart */}
            <div className="p-10 md:p-14">
              <p className="text-xs text-[#AAAAAA] mb-6 tracking-wide">
                {activePlan.badge} 플랜 · 누적 순이익 시뮬레이션 (단위: 만 원)
              </p>
              <RoiGraph visible={roiVisible} planId={selectedPlan} />
            </div>
          </div>
        </div>

        {/* ── Royalty Benefits ── */}
        <div
          className="transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transitionDelay: '900ms' }}
        >
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] uppercase text-[#AAAAAA] mb-3">월 로열티 50만 원에 포함된 혜택</p>
            <h3
              className="text-2xl md:text-3xl text-[#1A1A1A]"
              style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}
            >
              로열티 그 이상의 가치
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {royaltyBenefits.map((b, i) => (
              <div
                key={b.title}
                className="flex gap-5 p-6 bg-white border border-[#EBEBEB] hover:border-[#C9A96E] transition-colors duration-300 cursor-default group"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'opacity 0.7s ease-out, transform 0.7s ease-out, border-color 0.3s',
                  transitionDelay: `${i * 80 + 1000}ms`,
                }}
              >
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 border border-[#EBEBEB] group-hover:border-[#C9A96E] group-hover:bg-[#C9A96E] transition-all duration-300">
                  <i className={`${b.icon} text-lg text-[#AAAAAA] group-hover:text-white transition-colors duration-300`} />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-[#1A1A1A] mb-1.5">{b.title}</h4>
                  <p className="text-xs text-[#888] leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="h-px flex-1 bg-[#EBEBEB]" />
            <span className="text-xs text-[#BBBBBB] whitespace-nowrap px-4">월 로열티 500,000원 (고정) · 매출 연동 없음</span>
            <div className="h-px flex-1 bg-[#EBEBEB]" />
          </div>
        </div>

      </div>
    </section>
  );
}

// ── Sub Components ─────────────────────────────────────────────────────────────

function GuideHero() {
  const { ref, visible } = useScrollAnimation(0.1);

  return (
    <section
      className="relative pt-40 pb-24 md:pt-52 md:pb-32 overflow-hidden"
      style={{ background: '#1A1A1A' }}
      ref={ref}
    >
      <div className="absolute inset-0 opacity-50">
        <img
          src="https://public.readdy.ai/ai/img_res/a6e0e3a5f0505e2b87d53992285a5787.jpg"
          alt=""
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/30 via-[#1A1A1A]/20 to-[#1A1A1A]/70" />
      <FlowingWaves color="rgba(201,169,110,0.1)" lineCount={4} />
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.2)" size={160} />
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.15)" size={130} />

      <div className="relative z-10 max-w-[1440px] mx-auto px-8 md:px-16 text-center">
        <div
          className="flex items-center justify-center gap-2 mb-10 transition-all duration-700 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          <Link to="/franchise" className="text-[10px] tracking-[0.3em] uppercase text-white/30 hover:text-white/60 transition-colors cursor-pointer" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Franchise
          </Link>
          <span className="text-white/20 text-xs">/</span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A96E]" style={{ fontFamily: "'Montserrat', sans-serif" }}>창업 안내</span>
        </div>

        <div
          className="transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transitionDelay: '150ms' }}
        >
          <h1
            className="text-4xl md:text-6xl text-white mb-6 leading-tight"
            style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.1em' }}
          >
            창업 안내
          </h1>
          <p className="text-sm md:text-base text-white/50 leading-relaxed max-w-lg mx-auto" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.8' }}>
            선율과 함께하는 창업의 모든 과정을<br />
            단계별로 투명하게 안내해 드립니다.
          </p>
        </div>

        <div
          className="mx-auto mt-12 h-px bg-white/15 transition-all duration-1000 ease-out"
          style={{ width: visible ? '80px' : '0px', transitionDelay: '500ms' }}
        />

        <div
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-10 md:gap-20 transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transitionDelay: '600ms' }}
        >
          {[
            { value: '8단계', label: '개설 절차' },
            { value: '약 3개월', label: '평균 오픈 기간' },
            { value: '2,600만 원~', label: '최소 창업 비용' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p
                className="text-2xl md:text-3xl text-white mb-1"
                style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, letterSpacing: '-0.01em' }}
              >
                {stat.value}
              </p>
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/30" style={{ fontFamily: "'Montserrat', sans-serif" }}>{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProcessTimeline() {
  const { ref, visible } = useScrollAnimation(0.08);
  const [activeStep, setActiveStep] = useState<number | null>(null);

  return (
    <section className="py-28 md:py-36 bg-white overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="top-left" color="rgba(201,169,110,0.08)" size={150} />
      <BotanicalCorner position="bottom-right" color="rgba(201,169,110,0.08)" size={140} />
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <div
          className="text-center mb-12 transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Step by Step</p>
          <h2
            className="text-3xl md:text-4xl text-[#222222] mb-4"
            style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}
          >
            개설 절차 8단계
          </h2>
          <p className="text-sm text-[#888] max-w-md mx-auto leading-relaxed" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>
            문의부터 오픈까지, 선율 본사가 모든 단계를 함께합니다.<br />
            평균 소요 기간은 약 3개월입니다.
          </p>
        </div>
        <div className="mb-14">
          <GoldLineDivider delay={0.2} />
        </div>

        <div className="relative">
          <div
            className="hidden lg:block absolute left-[calc(50%-0.5px)] top-0 w-px bg-[#EBEBEB] transition-all duration-1500 ease-out"
            style={{ height: visible ? '100%' : '0%', transitionDelay: '300ms' }}
          />
          <div className="flex flex-col gap-0">
            {processSteps.map((step, i) => {
              const isLeft = i % 2 === 0;
              const isActive = activeStep === i;
              return (
                <div
                  key={step.step}
                  className="relative flex flex-col lg:flex-row items-stretch lg:items-center"
                  style={{
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(40px)',
                    transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
                    transitionDelay: `${i * 100 + 400}ms`,
                  }}
                >
                  <div className="hidden lg:block lg:w-[calc(50%-40px)] lg:pr-10">
                    {isLeft ? (
                      <button
                        className={`w-full text-left p-8 border transition-all duration-300 cursor-pointer ${isActive ? 'border-[#1A1A1A] bg-[#1A1A1A]' : 'border-[#EBEBEB] bg-white hover:border-[#1A1A1A]'}`}
                        onClick={() => setActiveStep(isActive ? null : i)}
                      >
                        <StepCard step={step} isActive={isActive} />
                      </button>
                    ) : (
                      <div />
                    )}
                  </div>

                  <div className="hidden lg:flex w-20 flex-shrink-0 items-center justify-center relative z-10">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${isActive ? 'bg-[#C9A96E] border-[#C9A96E]' : 'bg-white border-[#DDDDDD] hover:border-[#C9A96E]'}`}>
                      <span className={`text-[10px] font-semibold tracking-wider transition-colors duration-300 ${isActive ? 'text-white' : 'text-[#AAAAAA]'}`}>{step.step}</span>
                    </div>
                  </div>

                  <div className="hidden lg:block lg:w-[calc(50%-40px)] lg:pl-10">
                    {!isLeft ? (
                      <button
                        className={`w-full text-left p-8 border transition-all duration-300 cursor-pointer ${isActive ? 'border-[#1A1A1A] bg-[#1A1A1A]' : 'border-[#EBEBEB] bg-white hover:border-[#1A1A1A]'}`}
                        onClick={() => setActiveStep(isActive ? null : i)}
                      >
                        <StepCard step={step} isActive={isActive} />
                      </button>
                    ) : (
                      <div />
                    )}
                  </div>

                  <button
                    className={`lg:hidden w-full text-left p-6 border transition-all duration-300 cursor-pointer ${isActive ? 'border-[#1A1A1A] bg-[#1A1A1A]' : 'border-[#EBEBEB] bg-white'}`}
                    onClick={() => setActiveStep(isActive ? null : i)}
                  >
                    <StepCard step={step} isActive={isActive} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function StepCard({ step, isActive }: { step: typeof processSteps[0]; isActive: boolean }) {
  return (
    <>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 flex items-center justify-center flex-shrink-0 ${isActive ? 'text-[#C9A96E]' : 'text-[#AAAAAA]'}`}>
            <i className={`${step.icon} text-xl`} />
          </div>
          <div>
            <span className={`text-[10px] tracking-[0.2em] uppercase block mb-0.5 ${isActive ? 'text-white/40' : 'text-[#AAAAAA]'}`}>Step {step.step}</span>
            <h3 className={`text-base font-semibold leading-tight ${isActive ? 'text-white' : 'text-[#1A1A1A]'}`}>{step.title}</h3>
          </div>
        </div>
        <span className={`text-[10px] tracking-widest px-3 py-1 rounded-full whitespace-nowrap flex-shrink-0 ml-2 ${isActive ? 'bg-white/10 text-white/60' : 'bg-[#F5F5F5] text-[#999]'}`}>
          {step.period}
        </span>
      </div>
      <p className={`text-sm leading-relaxed mb-4 ${isActive ? 'text-white/70' : 'text-[#666]'}`}>{step.desc}</p>
      <ul className="flex flex-col gap-1.5">
        {step.details.map((d) => (
          <li key={d} className="flex items-center gap-2">
            <span className={`w-1 h-1 rounded-full flex-shrink-0 ${isActive ? 'bg-[#C9A96E]' : 'bg-[#CCCCCC]'}`} />
            <span className={`text-xs ${isActive ? 'text-white/60' : 'text-[#888]'}`}>{d}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

function SupportHighlights() {
  const { ref, visible } = useScrollAnimation(0.15);
  return (
    <section className="py-28 bg-white overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.1)" size={150} />
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <div
          className="text-center mb-10 transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>본사 지원</p>
          <h2 className="text-3xl md:text-4xl text-[#222222]" style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}>
            혼자가 아닙니다
          </h2>
        </div>
        <div className="mb-12">
          <GoldLineDivider delay={0.2} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {supportHighlights.map((s, i) => (
            <div
              key={s.title}
              className="group border border-[#EBEBEB] p-8 hover:border-[#222222] transition-all duration-300 cursor-default"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.7s ease-out, transform 0.7s ease-out, border-color 0.3s',
                transitionDelay: `${i * 100 + 200}ms`,
              }}
            >
              <div className="w-12 h-12 flex items-center justify-center mb-6 border border-[#EBEBEB] group-hover:border-[#222222] group-hover:bg-[#222222] transition-all duration-300">
                <i className={`${s.icon} text-xl text-[#AAAAAA] group-hover:text-white transition-colors duration-300`} />
              </div>
              <h3 className="text-sm font-semibold text-[#222222] mb-3" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}>{s.title}</h3>
              <p className="text-xs text-[#888] leading-relaxed" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GuideCta() {
  const { ref, visible } = useScrollAnimation(0.2);
  return (
    <section className="py-28 bg-[#1A1A1A] overflow-hidden relative" ref={ref}>
      <FlowingWaves color="rgba(201,169,110,0.08)" lineCount={3} />
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.18)" size={150} />
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.15)" size={130} />
      <div className="relative z-10 max-w-[1440px] mx-auto px-8 md:px-16 text-center">
        <div
          className="transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-6" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Ready to Start?</p>
          <h2
            className="text-3xl md:text-5xl text-white mb-6 leading-tight"
            style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}
          >
            지금 첫 걸음을<br />
            <span className="text-white/40">함께 내딛으세요.</span>
          </h2>
          <p className="text-sm text-white/40 leading-relaxed max-w-md mx-auto mb-12" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>
            선율 전담 컨설턴트가 창업의 처음부터 끝까지<br />
            맞춤형으로 안내해 드립니다.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/community/inquiry"
              className="px-10 py-4 bg-[#C9A96E] text-white text-[10px] tracking-[0.2em] uppercase font-medium rounded-full hover:bg-[#b8955a] transition-colors duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              가맹 문의하기
            </Link>
            <Link
              to="/franchise/revenue"
              className="px-10 py-4 border border-white/20 text-white/60 text-[10px] tracking-[0.2em] uppercase font-medium rounded-full hover:border-white/50 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              수익 시뮬레이션 보기
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function FranchiseGuidePage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <GuideHero />
        <ProcessTimeline />
        <InvestmentPlanSection />
        <SupportHighlights />
        <GuideCta />
      </main>
      <Footer />
    </div>
  );
}